import React, { ReactElement } from 'react';

import { NavLink } from 'react-router-dom';

import styles from './organization-menu-styles.module.scss';

import { useUserData } from 'context';
import { ROLE_NAME } from 'enums';
import { IOrganization, PATHS } from 'types';
import { Avatar, StatusBadge, Util } from 'views/components';
import { formatDate } from 'views/pages/ManageOrganization/SubscriptionManagement/formatDate';

interface IOrganizationInfoProps {
  organization: IOrganization;
}

const OrganizationInfo = React.memo((props: IOrganizationInfoProps): ReactElement => {
  const { organization } = props;
  const { name, url } = organization;

  const { user, billingStatus, frozenOrganization } = useUserData();

  const isAdmin = user?.role.name === ROLE_NAME.ADMIN;
  const isOrganizationFrozen =
    Boolean(frozenOrganization) || billingStatus?.accessStatus === 'FROZEN';
  const isOrganizationSettingsAvailable = isOrganizationFrozen
    ? isAdmin
    : user?.role.name !== ROLE_NAME.MENTEE;
  const isPaymentDue = billingStatus?.accessStatus === 'PAYMENT_DUE';
  const paymentDueLabel = billingStatus?.gracePeriodEndsAt
    ? `Payment due · ${formatDate(billingStatus.gracePeriodEndsAt)}`
    : 'Payment due';

  const organizationInfoContent = (
    <>
      <Util marginRight="0.5rem">
        <Avatar size="tiny" image={url} mode="organization" organizationName={name} />
      </Util>
      <span className={styles['organization-info__name']}>{name}</span>
    </>
  );

  const organizationInfo = isOrganizationSettingsAvailable ? (
    <NavLink
      className={`${styles['organization-info']} ${styles['organization-info--link']}`}
      to={PATHS.MANAGE_ORGANIZATION}
    >
      {organizationInfoContent}
    </NavLink>
  ) : (
    <div className={styles['organization-info']}>{organizationInfoContent}</div>
  );

  return (
    <div className={styles['organization-info-wrapper']}>
      {organizationInfo}
      {isPaymentDue && (
        <StatusBadge
          className={styles['organization-info__payment-due']}
          size="small"
          variant="warning"
        >
          {isAdmin ? (
            <NavLink to={PATHS.MANAGE_ORGANIZATION_SUBSCRIPTION_MANAGEMENT}>
              {paymentDueLabel}
            </NavLink>
          ) : (
            paymentDueLabel
          )}
        </StatusBadge>
      )}
    </div>
  );
});

export { OrganizationInfo };
